import { Dataset, getDataset } from '../data/datasets'
import { ReportConfig, AggregateFn } from '../data/reports'
import { getMergedFields, buildJoinedRows, getJoin } from '../data/joins'

export interface GroupedRow {
  group: string
  value: number
}

export interface ReportResult {
  dataset: Dataset
  columns: { key: string; label: string }[]
  rows: Record<string, string | number>[]
  grouped: GroupedRow[] | null
  metricLabel: string
}

function applyFilters(
  config: ReportConfig,
  rows: Record<string, string | number>[],
) {
  return rows.filter((row) =>
    config.filters.every((f) => {
      if (!f.value) return true
      const cell = String(row[f.field] ?? '').toLowerCase()
      const val = f.value.toLowerCase()
      return f.operator === 'equals' ? cell === val : cell.includes(val)
    }),
  )
}

function aggregate(fn: AggregateFn, values: number[]): number {
  if (fn === 'count') return values.length
  const sum = values.reduce((a, b) => a + b, 0)
  if (fn === 'sum') return Math.round(sum * 10) / 10
  return values.length ? Math.round((sum / values.length) * 10) / 10 : 0
}

export function runReport(config: ReportConfig): ReportResult | null {
  const dataset = getDataset(config.datasetId)
  if (!dataset) return null

  const joinId =
    config.joinId && getJoin(config.joinId)?.baseId === config.datasetId
      ? config.joinId
      : null
  const fields = getMergedFields(config.datasetId, joinId)
  const sourceRows = joinId
    ? buildJoinedRows(config.datasetId, joinId)
    : dataset.rows

  const fieldMap = new Map(fields.map((f) => [f.key, f]))
  const selectedColumns = (
    config.columns.length ? config.columns : fields.map((f) => f.key)
  )
    .filter((c) => fieldMap.has(c))
    .map((c) => ({ key: c, label: fieldMap.get(c)!.label }))

  const filtered = applyFilters(config, sourceRows)

  let grouped: GroupedRow[] | null = null
  if (config.groupBy && fieldMap.has(config.groupBy)) {
    const buckets = new Map<string, number[]>()
    for (const row of filtered) {
      const key = String(row[config.groupBy] ?? '—')
      const list = buckets.get(key) ?? []
      if (config.metricField && config.metricFn !== 'count') {
        list.push(Number(row[config.metricField] ?? 0))
      } else {
        list.push(1)
      }
      buckets.set(key, list)
    }
    grouped = Array.from(buckets.entries())
      .map(([group, values]) => ({
        group,
        value: aggregate(config.metricFn, values),
      }))
      .sort((a, b) => b.value - a.value)
  }

  const metricLabel =
    config.metricFn === 'count'
      ? 'Record Count'
      : `${config.metricFn === 'avg' ? 'Avg' : 'Sum'} ${config.metricField ? (fieldMap.get(config.metricField)?.label ?? '') : ''}`

  return {
    dataset,
    columns: selectedColumns,
    rows: filtered.map((row) => {
      const out: Record<string, string | number> = {}
      selectedColumns.forEach((c) => (out[c.key] = row[c.key]))
      return out
    }),
    grouped,
    metricLabel,
  }
}
